import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import { useAuth } from '../../../lib/auth';
import CategoriesTabs from '../../../components/tabs/CategoriesTabs.tsx';
import { WorksheetCard } from '../../../components/cards/WorksheetCard.tsx';
import { EmptyState } from '../../../components/emptyStates/EmptyState.tsx';
import { useParams } from 'react-router';
import { useFetchRootCategories, useFetchWorksheets } from '../../../lib/fetches.ts';
import { Color, Worksheet, WorksheetAgeCategory } from '../../../lib/types.ts';
import { API_URL } from '../../../lib/api.ts';
import API from '../../../lib/api.ts';
import Skeleton from '../../../components/Skeleton.tsx';
import { useMemo } from 'react';

const DEFAULT_CATEGORY_COLOR: Color = 'turquoise';

const FALLBACK_AGE: WorksheetAgeCategory = {
    slug: 'ostatni',
    name: 'Ostatní',
    order: Number.MAX_SAFE_INTEGER,
};

function compareAgeCategoryOrder(a: WorksheetAgeCategory, b: WorksheetAgeCategory): number {
    if (a.order !== b.order) return a.order - b.order;
    return a.slug.localeCompare(b.slug);
}

/** Prefer `categories`, then singular `category`, then flat legacy fields. */
function getLectureCategories(
    w: Worksheet
): { name: string; slug: string }[] {
    if (w.categories?.length) {
        return w.categories.map(({ name, slug }) => ({ name, slug }));
    }
    if (w.category) {
        return [{ name: w.category.name, slug: w.category.slug }];
    }
    const slug = w.category_slug;
    const name = w.category_name;
    if (slug || name) {
        return [{ name: name ?? 'Kategorie', slug: slug ?? '' }];
    }
    return [];
}

/** Prefer `age_categories`, then singular `age_category`, else one synthetic bucket. */
function getAgeCategories(w: Worksheet): WorksheetAgeCategory[] {
    if (w.age_categories?.length) {
        return [...w.age_categories].sort(compareAgeCategoryOrder);
    }
    if (w.age_category) {
        return [w.age_category];
    }
    return [FALLBACK_AGE];
}

function getBadgeName(w: Worksheet, activeCategorySlug: string): string {
    const lectures = getLectureCategories(w);
    if (activeCategorySlug) {
        const match = lectures.find((c) => c.slug === activeCategorySlug);
        if (match) return match.name;
    }
    if (lectures.length === 0) return 'Kategorie';
    if (lectures.length === 1) return lectures[0].name;
    return lectures.map((c) => c.name).join(', ');
}

function getBadgeColor(
    w: Worksheet,
    activeCategorySlug: string,
    slugToColor: Map<string, Color>
): Color {
    const lectures = getLectureCategories(w);
    const slugForColor =
        activeCategorySlug &&
        lectures.some((c) => c.slug === activeCategorySlug)
            ? activeCategorySlug
            : lectures[0]?.slug;
    if (slugForColor) {
        const fromRoot = slugToColor.get(slugForColor);
        if (fromRoot) return fromRoot;
    }
    return w.category?.color ?? w.category_color ?? DEFAULT_CATEGORY_COLOR;
}

function getOpenPdfUrl(w: Worksheet) {
    if (w.pdf?.url) return w.pdf.url;
    return `${API_URL}/worksheets/${w.slug}/pdf`;
}

function filenameFromContentDisposition(value: string | undefined) {
    if (!value) return undefined;
    const match =
        /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(
            value
        );
    const encoded = match?.[1];
    const quoted = match?.[2];
    const bare = match?.[3];
    const raw = encoded ?? quoted ?? bare;
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw.trim());
    } catch {
        return raw.trim();
    }
}

async function downloadWorksheetPdf(slug: string) {
    const res = await API.get(`/worksheets/${slug}/pdf`, {
        responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const name =
        filenameFromContentDisposition(res.headers?.['content-disposition']) ??
        `${slug}.pdf`;

    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function WorksheetsPage() {
    useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'WorksheetsPage',
    });

    const { category = '' } = useParams();

    const { data: worksheets, isLoading } = useFetchWorksheets();
    const { data: rootCategories } = useFetchRootCategories();

    const slugToColor = useMemo(() => {
        const m = new Map<string, Color>();
        for (const c of rootCategories ?? []) {
            m.set(c.slug, c.color);
        }
        return m;
    }, [rootCategories]);

    const filteredWorksheets =
        category.length > 0
            ? (worksheets ?? []).filter((w) =>
                  getLectureCategories(w).some((c) => c.slug === category)
              )
            : (worksheets ?? []);

    const grouped = new Map<
        string,
        { key: string; name: string; order: number; items: Worksheet[] }
    >();
    const sectionKeys: string[] = [];

    for (const w of filteredWorksheets) {
        for (const age of getAgeCategories(w)) {
            const order = age.order;
            const existing = grouped.get(age.slug);
            if (!existing) {
                grouped.set(age.slug, {
                    key: age.slug,
                    name: age.name,
                    order,
                    items: [w],
                });
                sectionKeys.push(age.slug);
                continue;
            }
            if (order < existing.order) {
                existing.order = order;
            }
            if (!existing.items.some((item) => item.slug === w.slug)) {
                existing.items.push(w);
            }
        }
    }

    sectionKeys.sort((ka, kb) => {
        const a = grouped.get(ka);
        const b = grouped.get(kb);
        if (!a || !b) return 0;
        if (a.order !== b.order) return a.order - b.order;
        return ka.localeCompare(kb);
    });

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'mx-auto box-content max-w-6xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="mb-12">
                    <h1>Pracovní listy</h1>
                    <p className="text-lg text-ink-800">
                        Vyberte si materiály ke stažení.
                    </p>
                </div>

                <div className="mb-12">
                    <CategoriesTabs basePath="/pracovni-listy" filter={(category) => category.allow_worksheets} />
                </div>

                {isLoading ? (
                    <div className="space-y-12 pb-16">
                        {[0, 1, 2].map((idx) => (
                            <section key={idx}>
                                <Skeleton className="mb-6 h-7 w-48 rounded-xl" />
                                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                                    <Skeleton className="h-28 w-full rounded-3xl" />
                                    <Skeleton className="h-28 w-full rounded-3xl" />
                                </div>
                            </section>
                        ))}
                    </div>
                ) : sectionKeys.length === 0 ? (
                    <EmptyState>
                        Tato kategorie zatím neobsahuje žádné pracovní listy.
                    </EmptyState>
                ) : (
                    <div className="space-y-12 pb-16">
                        {sectionKeys.map((key) => {
                            const section = grouped.get(key);
                            if (!section) return null;

                            return (
                                <section key={key}>
                                    <h4 className="mb-6">{section.name}</h4>
                                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                                    {section.items.map((w) => (
                                        <WorksheetCard
                                            key={`${section.key}-${w.slug}`}
                                            title={w.name}
                                            description={w.description}
                                            badge={getBadgeName(w, category)}
                                            color={getBadgeColor(
                                                w,
                                                category,
                                                slugToColor
                                            )}
                                            openHref={getOpenPdfUrl(w)}
                                            onDownload={() =>
                                                downloadWorksheetPdf(w.slug)
                                            }
                                        />
                                    ))}
                                </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </main>
        </AuthLayout>
    );
}

